import { initializeFaro, getWebInstrumentations, FetchTransport, ConsoleInstrumentation, ConsoleTransport, LogLevel } from "@grafana/faro-web-sdk";
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import {userDataStore} from './UserData.Store'

class FaroService {
  constructor() {
    this.faroInstance = null;
    this.currentSessionId = "";
    this.userType = null;
    this.traceId = null;
  }

  /**
   * Initializes the Faro instance with the given session ID.
   * @param {string} sessionId - The session ID for tracking
   */
  initialize(sessionId, userType) {
    this.currentSessionId = sessionId;
    this.userType = userType;
    sessionStorage.setItem("sessionId", sessionId);

    // Clear any existing Faro session
    sessionStorage.removeItem("com.grafana.faro.session");

    const traceIdFromResponse = sessionStorage.getItem("traceId"); 
    if (traceIdFromResponse) {
      this.traceId = traceIdFromResponse;
    }

    const isFaroEnabled = userDataStore.getters.envValues.faroEnabled === 'true';

    if (!isFaroEnabled) {
      console.log("[FARO] Faro service is disabled due to environment configuration.");
      return; // Exit early if Faro is disabled
    }

    this.faroInstance = initializeFaro({
      transports: [
        new FetchTransport({
        url: `${window.location.origin}/faro`,
          bufferSize: parseInt(userDataStore.getters.envValues.faroBufferSize),
          apiKey: userDataStore.getters.envValues.faroApiKey,
        }),
        new ConsoleTransport(),
      ],
      isolate: true,
      app: {
        name: "VKYC",
        version: "1.0.0",
        environment: userDataStore.getters.envValues.faroEnvironment,
      },
      user: {
        attributes: {
          'type': userType
        }
      },
      sessionTracking: {
        enabled: true,
        generateSessionId: () => sessionStorage.getItem("sessionId") || "",
      },
      instrumentations: [
        new ConsoleInstrumentation({
          disabledLevels: [LogLevel.TRACE],
        }),
        new TracingInstrumentation({
          traceId: this.traceId 
        }),
        ...getWebInstrumentations({
          captureConsole: true,
          captureErrors: true,
          captureNetwork: true,
          capturePerformance: true,
        })
      ],
      consoleInstrumentation: {
        captureConsoleDisabledLevels: [LogLevel.DEBUG, LogLevel.INFO, LogLevel.LOG],
      }
    });

    console.log("[FARO] Initialized with session ID:", sessionId);
  }

  /**
   * Ends the current session.
   * This clears the stored session so that new telemetry will start a new session.
   */
  endSession() {
    this.currentSessionId = "";
    sessionStorage.removeItem("sessionId");
    sessionStorage.removeItem("com.grafana.faro.session");

    console.log("[FARO] Ended current session.");

    if (
      this.faroInstance &&
      this.faroInstance.api &&
      typeof this.faroInstance.api.resetSession === "function"
    ) {
      this.faroInstance.api.resetSession();
    }
  }

  /**
   * Returns the Faro instance.
   */
  getInstance() {
    return this.faroInstance;
  }
  /**
    * Logs an event to Faro service with the given parameters.
    *
    * This method retrieves the Faro service instance and attempts to push an event
    * with the provided event name, identifier, value, and message. If the Faro instance
    * is not initialized or an error occurs during the push, appropriate error handling
    * will be performed.
    *
    * @param {string} eventName - The name of the event to be logged in Faro.
    * @param {string} identifier - A unique identifier for the event.
    * @param {string} value - The value associated with the event.
    * @param {string} message - A message providing additional context about the event.
    * 
    */
    logEvent(eventName, identifier, value, message) {
      if(this.faroInstance) {
        try {
        this.faroInstance.api.pushEvent(eventName, {
            identifier,
            value,
            message
        });
          console.log(`Faro event for ${eventName} ${identifier} logged successfully`);
        } catch (error) {
          console.log('error while pushing event to faro', error);
        }
      } else {
        console.error("Error: [Sending Data] Faro Instance not initialized!")
      } 
    }
}

export default new FaroService();
